import {
  SET_STUDENTS,
  RESET_STUDENTS,
  SET_CURRENT_STUDENT,
  SET_DUMMY_EMAIL_CREDENTIAL,
  SET_DUMMY_EMAIL_CREDENTIALS,
  RESET_DUMMY_EMAIL_CREDENTIALS,
  SET_ASSESSMENT_DATA,
  SET_OVERVIEW_DATA,
  SET_IELTS,
  SET_STUDENT_CLASSES,
  LOADING_AVERAGE_SCORE_SKILLS,
  SET_AVERAGE_SCORE_SKILLS,
  SET_PENTAGON,
  SET_CHART_DATE,
  RESET_CHART_DATE,
} from "../constants/students";

import { STUDENT } from "@/constants/userRoles";

import { propOr, map, pathOr } from "ramda";
import axios from "@/utils/axios";
import {
  SET_LESSONS_TOTAL_TIME_PRACTICED,
  SET_EPS,
  SET_LESSONS_TIME_PRACTICED,
  SET_STUDYSET_TIME_PRACTICED,
} from "../constants";
import { getAbstractGetChartData } from "@/store/modules/chartsFragment";
import { extractDate } from "@/utils/dateInterval";
import { startOfMonth, toDate } from "date-fns";

const initialState = () => ({
  students: {
    results: [],
  },
  currentStudent: {},
  dummyEmailCredential: {},
  dummyEmailCredentials: [],
  lessonsTotalTimePracticed: [],
  lessonsTimePracticed: [],
  // Chart data for number of study-set practiced
  studySetTimePracticed: [],
  eps: [],
  pentagon: {},
  ielts: {},
  assessmentData: {},
  studentClasses: {},
  overviewData: {},
  highestAverageScoreSkills: null,
  lowestAverageScoreSkills: null,
  loadingAverageScoreSkills: false,
  currentChartDate: {
    startDate: startOfMonth(Date.now()),
    endDate: toDate(Date.now()),
  },
});

const state = initialState();

const getters = {
  paginateStudents: propOr({}, "students"),
  currentStudent: propOr({}, "currentStudent"),
  dummyEmailCredential: propOr({}, "dummyEmailCredential"),
  loadingAverageScoreSkills: propOr(false, "loadingAverageScoreSkills"),
  currentChartDate: propOr({}, "currentChartDate"),
};

const actions = {
  getStudents({ commit, dispatch }, { page = 1, search = null } = {}) {
    return dispatch(
      "organization/_getCurrentOrganizationUsersPage",
      {
        page,
        search,
        roles: STUDENT,
      },
      { root: true }
    ).then(response => commit(SET_STUDENTS, response.data));
  },
  reset({ commit }) {
    return commit(RESET_STUDENTS);
  },
  resetdummyEmailCredentials({ commit }) {
    return commit(RESET_DUMMY_EMAIL_CREDENTIALS);
  },
  getStudent({ dispatch }, { studentId, organizationId }) {
    return axios
      .get(`v1/organizations/${organizationId}/users/${studentId}`)
      .then(response => dispatch("setCurrentStudent", response.data));
  },
  getStudentDummyEmailCredential({ commit }, { studentId, organizationId }) {
    return axios
      .get(`v1/organizations/${organizationId}/${studentId}/dummy-credential`)
      .then(response => commit(SET_DUMMY_EMAIL_CREDENTIAL, response.data));
  },
  getAllDummyEmailCredentials({ commit, rootGetters }, _organizationId) {
    const organizationId = _organizationId || rootGetters["organization/currentOrganizationId"];

    return axios
      .get(`v1/organizations/${organizationId}/users-dummy-credential`)
      .then(response => commit(SET_DUMMY_EMAIL_CREDENTIALS, response.data));
  },
  setCurrentStudent({ commit }, student) {
    return commit(SET_CURRENT_STUDENT, student);
  },
  getChartData({ commit, getters, dispatch }, { studentId: _studentId = null, scope, from, to } = {}) {
    const studentId = _studentId || getters.currentStudent.id;
    if (studentId === undefined || studentId === null) {
      return Promise.reject(new Error("Student ID is null or undefined"));
    }

    const params = {
      studentId,
      from: extractDate(from),
      to: extractDate(to),
      scope,
    };

    const getLessonsTotalTimePracticed = getAbstractGetChartData({
      axios,
      commit,
      mutationId: SET_LESSONS_TOTAL_TIME_PRACTICED,
      url: `v1/reports/user/${studentId}/lessons_practiced`,
      ...params,
    });

    const getEPS = axios
      .get(`v1/reports/user/${studentId}/eps`, { params })
      .then(response => commit(SET_EPS, response.data));

    return Promise.all([
      getLessonsTotalTimePracticed,
      getEPS,
      dispatch("getAssessmentData", params),
      dispatch("getOverviewData", params),
      dispatch("getIELTS", params),
      dispatch("getAverageScoreSkills", params),
      dispatch("getPentagon", params),
    ]).catch(error => {
      throw error;
    });
  },
  getAssessmentData({ commit, getters }, { studentId: _studentId = null, scope, from, to }) {
    const studentId = _studentId || getters.currentStudent.id;
    const params = {
      from,
      to,
      scope,
    };

    return axios
      .get(`v1/reports/user/${studentId}/assessment_test`, { params })
      .then(response => {
        const assessmentDatas = response.data;
        let last_assessment = null;
        if (assessmentDatas.length > 0) {
          last_assessment = assessmentDatas[assessmentDatas.length - 1];
        }
        commit(SET_ASSESSMENT_DATA, last_assessment);
      });
  },
  getOverviewData({ commit, getters }, { studentId: _studentId = null, scope, from, to }) {
    const studentId = _studentId || getters.currentStudent.id;
    const params = {
      from,
      to,
      scope,
    };

    return axios
      .get(`v1/reports/user/${studentId}/overview`, { params })
      .then(response => commit(SET_OVERVIEW_DATA, response.data));
  },
  getIELTS({ commit, getters }, { studentId: _studentId = null, scope, from, to }) {
    const studentId = _studentId || getters.currentStudent.id;
    const params = {
      from,
      to,
      scope,
    };

    return axios
      .get(`v1/reports/user/${studentId}/ielts`, { params })
      .then(response => commit(SET_IELTS, response.data));
  },
  getPentagon({ commit, getters }, { studentId: _studentId = null }) {
    const studentId = _studentId || getters.currentStudent.id;
    const url = `v1/reports/user/${studentId}/pentagon`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_PENTAGON,
      name: "pentagon",
    });
  },
  getStudentClasses(
    { commit, getters, rootGetters },
    { organizationId: _organizationId, studentId: _studentId = null } = {}
  ) {
    const organizationId = _organizationId || rootGetters["organization/currentOrganizationId"];
    const studentId = _studentId || getters.currentStudent.id;

    return axios
      .get(`v1/groups/${organizationId}/${studentId}/groups`)
      .then(response => commit(SET_STUDENT_CLASSES, response.data));
  },
  async getAverageScoreSkills({ commit, getters }, { studentId: _studentId = null, from, to } = {}) {
    const studentId = _studentId || getters.currentStudent.id;
    const url = `v1/reports/user/${studentId}/skill_score`;

    commit(LOADING_AVERAGE_SCORE_SKILLS, true);

    try {
      await axios.get(url, { params: { from, to } }).then(response => {
        const chartData = pathOr([{ name: "lowest_average_score_skills", data: [] }], ["data"])(response);
        return commit(SET_AVERAGE_SCORE_SKILLS, chartData);
      });
    } finally {
      commit(LOADING_AVERAGE_SCORE_SKILLS, false);
    }
  },
  setChartDate({ commit }, obj) {
    return commit(SET_CHART_DATE, obj);
  },
  resetChartDate({ commit }) {
    return commit(RESET_CHART_DATE);
  },
};

const mutations = {
  [SET_STUDENTS](state, paginatedStudents) {
    const paginatedStudents_parse = {
      count: paginatedStudents.count,
      results: [],
    };
    paginatedStudents.results.forEach(student => {
      if (student.groups.length > 0)
        student.groups.forEach(group => {
          const obj = { ...student, group };
          paginatedStudents_parse["results"].push(obj);
        });
      else paginatedStudents_parse["results"].push(student);
    });
    state.students = { ...paginatedStudents_parse };
  },
  [RESET_STUDENTS](state) {
    Object.assign(state, initialState());
  },
  [SET_CURRENT_STUDENT](state, student) {
    state.currentStudent = student;
  },
  [SET_DUMMY_EMAIL_CREDENTIAL](state, dummyEmailCredential) {
    state.dummyEmailCredential = dummyEmailCredential;
  },
  [SET_DUMMY_EMAIL_CREDENTIALS](state, dummyEmailCredentials) {
    state.dummyEmailCredentials = dummyEmailCredentials;
  },
  [RESET_DUMMY_EMAIL_CREDENTIALS](state) {
    state.dummyEmailCredentials = [];
  },
  [SET_LESSONS_TOTAL_TIME_PRACTICED](state, lessonsTotalTimePracticed) {
    const sec2minute = sec => sec / 60;
    lessonsTotalTimePracticed[0]["data"] = map(sec2minute, lessonsTotalTimePracticed[0]["data"]);
    state.lessonsTotalTimePracticed = lessonsTotalTimePracticed;
  },
  [SET_LESSONS_TIME_PRACTICED](state, lessonsTimePracticed) {
    const sec2minute = sec => sec / 60;
    lessonsTimePracticed[0]["data"] = map(sec2minute, lessonsTimePracticed[0]["data"]);
    state.lessonsTimePracticed = lessonsTimePracticed;
  },
  [SET_STUDYSET_TIME_PRACTICED](state, studySetTimePracticed) {
    const sec2minute = sec => sec / 60;
    studySetTimePracticed[0]["data"] = map(sec2minute, studySetTimePracticed[0]["data"]);
    state.studySetTimePracticed = studySetTimePracticed;
  },
  [SET_EPS](state, [eps = {}] = []) {
    let cumulativeEps = 0;

    const normalizedEps = map(epsValue => {
      if (!epsValue) return cumulativeEps;

      return (cumulativeEps = epsValue);
    })(eps.data);

    state.eps = [{ ...eps, data: normalizedEps }];
  },
  [SET_ASSESSMENT_DATA](state, assessmentData) {
    state.assessmentData = assessmentData;
  },
  [SET_OVERVIEW_DATA](state, overviewData) {
    state.overviewData = { ...overviewData };
  },
  [SET_IELTS](state, ielts) {
    state.ielts = ielts;
  },
  [SET_PENTAGON](state, pentagon) {
    state.pentagon = pentagon;
  },
  [SET_STUDENT_CLASSES](state, studentClasses) {
    state.studentClasses = studentClasses;
  },
  [SET_AVERAGE_SCORE_SKILLS](state, averageScoreSkills) {
    const highScoreItems = Object.keys(averageScoreSkills)
      .filter(key => averageScoreSkills[key] >= 78)
      .map(key => ({ name: key, data: [averageScoreSkills[key]] }))
      .sort((first, second) => second.data[0] - first.data[0])
      .slice(0, 5);

    const lowScoreItems = Object.keys(averageScoreSkills)
      .filter(key => averageScoreSkills[key] < 78)
      .map(key => ({ name: key, data: [averageScoreSkills[key]] }))
      .sort((first, second) => first.data[0] - second.data[0])
      .slice(0, 5);

    state.highestAverageScoreSkills = highScoreItems;
    state.lowestAverageScoreSkills = lowScoreItems;
  },
  [LOADING_AVERAGE_SCORE_SKILLS](state, loading) {
    state.loadingAverageScoreSkills = loading;
  },
  [SET_CHART_DATE](state, obj) {
    state.currentChartDate = obj;
  },
  [RESET_CHART_DATE](state) {
    state.currentChartDate = {
      startDate: startOfMonth(Date.now()),
      endDate: toDate(Date.now()),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
